<template>
  <BaseModal
    :modalTitle="modalTitle"
    contentPadding="none">
    <div class="flex items-center justify-between gap-3 border-b border-border-subtle px-6 py-3">
      <span
        v-if="noticeText"
        class="text-sm text-text-secondary"
        data-testid="invoice-pdf-notice">
        {{ noticeText }}
      </span>
      <span v-else />
      <UiBaseButton
        :text="$t('W: Download PDF')"
        :isDisabled="!blob"
        data-testid="invoice-pdf-download"
        @click="download">
        <template #icon-left>
          <IconLucideDownload />
        </template>
      </UiBaseButton>
    </div>

    <div
      v-if="isLoading"
      class="flex items-center justify-center gap-2 px-6 py-10 text-sm text-text-muted">
      <IconLucideLoader2 class="h-4 w-4 animate-spin" />
      <span>{{ loadingText }}</span>
    </div>

    <div
      v-else-if="errorText"
      class="flex flex-col gap-2 px-6 py-10 text-center text-sm text-feedback-error"
      data-testid="invoice-pdf-error">
      <span>{{ errorText }}</span>
      <BcErrorMessage
        v-if="errorDetail"
        :message="errorDetail" />
    </div>

    <iframe
      v-else
      :src="frameUrl"
      :title="modalTitle"
      class="block w-full border-0 bg-surface-sunken"
      style="height: 70vh"
      data-testid="invoice-pdf-frame" />
  </BaseModal>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { mapState } from 'pinia'
import { getBlob, ref as storageRef } from 'firebase/storage'
import { storage } from '@/firebase/init'
import { useAccountStore } from '@/stores/accountStore'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { downloadBlob } from '@/globals/typescript/utils/downloadBlob'
import { reportError } from '@/globals/typescript/utils/reportError'
import { useToast } from '@/globals/typescript/utils/toast'

/**
 * Shows an invoice's PDF in the page, with the same "Download PDF" bar the report previews have.
 * "Se kladde" asks BC for the draft as it is right now (a copy stored when the draft was created goes
 * stale the moment anyone changes the draft or the order, and nothing re-syncs it); if BC cannot
 * deliver it, the last saved copy is shown instead and the bar says so (the copy a successful "Se kladde" stored last, or the one from creation). "Se faktura" shows the invoice's
 * stored PDF. The file is read as a blob, the same call the downloads use, so Download saves exactly
 * what is on screen under `downloadName`.
 */
export default {
  name: 'InvoicePdfPreviewModal',

  props: {
    modalTitle: { type: String, required: true },
    kind: { type: String as PropType<'draft' | 'invoice'>, required: true },
    orderID: { type: String, required: true },
    /** The invoice's side on a split order; absent for a normal order. */
    side: { type: String as PropType<'customer' | 'partner' | undefined>, default: undefined },
    /** The PDF stored on the invoice: what an invoice shows, and what a draft falls back to. */
    storedPath: { type: String, default: '' },
    /** Show the stored copy without asking BC — an invoice made on another BC connection must not reach it. */
    isStoredOnly: { type: Boolean, default: false },
    downloadName: { type: String, required: true },
  },

  data() {
    return {
      isLoading: true,
      errorText: '',
      errorDetail: '',
      noticeText: '',
      /** Set when the modal closes, so a request still in flight does not create an object URL nobody revokes. */
      isDisposed: false,
      blob: null as Blob | null,
      objectUrl: '',
    }
  },

  computed: {
    ...mapState(useAccountStore, ['labAccount']),

    /** The browser's own PDF toolbar is hidden: its download button saves the blob under a random id, and
     *  "Download PDF" above is the one that names the file. Browsers that ignore the flags just show theirs. */
    frameUrl(): string {
      return this.objectUrl ? `${this.objectUrl}#toolbar=0&navpanes=0` : ''
    },

    loadingText(): string {
      return this.kind === 'draft' ? this.$t('W: Henter kladden fra Business Central') : this.$t('W: Henter fakturaen')
    },
  },

  methods: {
    /** The draft's PDF, rendered by BC just now and stored; `error` is BC's own text when it refused. */
    async fetchFreshDraftPath(labAccountID: string): Promise<{ path: string; error: string }> {
      try {
        const result = await managementApi(
          'post',
          'lab-accounts/{labAccountID}/orders/{orderID}/bc-invoice/draft-pdf',
          {
            path: { labAccountID, orderID: this.orderID },
            query: { side: this.side },
          }
        )
        if (!result.success) return { path: '', error: result.message ?? '' }
        return { path: result.data.invoicePdfPath, error: '' }
      } catch (error) {
        // The API client never throws for a failed call; this is a bug on our side of it.
        reportError(error, { operation: 'invoice-draft-pdf-fetch', extra: { orderID: this.orderID } })
        return { path: '', error: '' }
      }
    },

    async readStoredBlob(path: string): Promise<Blob> {
      try {
        return await getBlob(storageRef(storage, path))
      } catch (error) {
        // The file comes straight from Storage in the browser, so nothing else records a denial or
        // a network failure here.
        reportError(error, { operation: 'invoice-pdf-preview', extra: { path } })
        throw error
      }
    },

    async load(): Promise<void> {
      const labAccountID = this.labAccount?.id
      let path = this.storedPath
      let draftError = ''
      if (this.kind === 'draft' && labAccountID && !this.isStoredOnly) {
        const fresh = await this.fetchFreshDraftPath(labAccountID)
        if (this.isDisposed) return
        if (fresh.path) {
          path = fresh.path
        } else {
          draftError = fresh.error
          if (this.storedPath) {
            this.noticeText = this.$t('W: Kladden kunne ikke hentes fra Business Central. Du ser den senest gemte kopi.')
          }
        }
      }

      if (!path) {
        this.errorText = this.$t('W: Kunne ikke hente kladden')
        this.errorDetail = draftError
        this.isLoading = false
        return
      }

      try {
        const blob = await this.readStoredBlob(path)
        if (this.isDisposed) return
        this.blob = blob
        this.objectUrl = URL.createObjectURL(blob)
      } catch {
        // Closed while the read was failing: nobody is left to tell.
        if (this.isDisposed) return
        this.errorText = this.kind === 'draft' ? this.$t('W: Kunne ikke hente kladden') : this.$t('W: Kunne ikke hente fakturaen')
        this.noticeText = ''
        useToast().error(this.errorText)
      } finally {
        this.isLoading = false
      }
    },

    download(): void {
      if (this.blob) downloadBlob(this.blob, this.downloadName)
    },
  },

  mounted(): void {
    void this.load()
  },

  beforeUnmount(): void {
    this.isDisposed = true
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
  },
}
</script>
